import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, Loader2, RotateCcw } from 'lucide-react';
import {
  DashboardWrapper,
  DashboardContainer,
  PageHeader,
  HeaderLeft,
  TitleRow,
  Title,
  Description,
  MetricsGrid,
  MetricCard,
  MetricHeader,
  MetricLabelGroup,
  MetricLabel,
  PulseDot,
  PulseCount,
  MetricValueRow,
  MetricValue,
  MetricSubtitle,
  MetricFooter,
  MetricFooterText,
  MetricArrow,
  EmptyState,
} from './adminDashboard.styles';
import { useAdminDashboardStats } from './hooks/useAdminDashboardStats';
import { RetryButton, SpinWrapper } from '../../../settings/appStyle/global.style';

const buildMetrics = (stats) => [
  {
    id: 'total',
    label: 'Total tickets',
    value: stats.closedTickets + stats.openTickets,
    subtitle: '',
    path: '/tickets',
  },
  {
    id: 'open',
    label: 'Open tickets',
    pulseCount: stats.unviewedQueries > 0 ? stats.unviewedQueries : 0,
    value: stats.openTickets,
    subtitle: stats.unviewedQueries > 0 ? `${stats.unviewedQueries} unviewed` : '',
    subtitleAccent: true,
    path: '/tickets',
    query: { status: 'OPEN' },
  },
  {
    id: 'closed',
    label: 'Closed tickets',
    value: stats.closedTickets,
    subtitle: '',
    path: '/tickets',
    query: { status: 'CLOSED' },
  },
  {
    id: 'clients',
    label: 'Total Clients',
    value: stats.totalClients,
    subtitle: '',
    path: '/clients',
  },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useAdminDashboardStats();

  const handleNavigate = (metric) => {
    const qs = metric.query ? `?${new URLSearchParams(metric.query).toString()}` : '';
    navigate(`${metric.path}${qs}`);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <EmptyState>
          <SpinWrapper>
            <Loader2 />
          </SpinWrapper>
          <p>Loading dashboard statistics…</p>
        </EmptyState>
      );
    }

    if (isError) {
      return (
        <EmptyState>
          <p>Failed to load dashboard statistics</p>
          <p>Please try again later</p>
          <RetryButton onClick={() => refetch()}>
            <RotateCcw />
            Retry
          </RetryButton>
        </EmptyState>
      );
    }

    const stats = data?.statistics;

    if (!stats) {
      return (
        <EmptyState>
          <p>No statistics available</p>
        </EmptyState>
      );
    }

    return (
      <MetricsGrid>
        {buildMetrics(stats).map((metric) => (
          <MetricCard key={metric.id} onClick={() => handleNavigate(metric)}>
            <MetricHeader>
              <MetricLabelGroup>
                <MetricLabel>{metric.label}</MetricLabel>
              </MetricLabelGroup>

              {metric.pulseCount > 0 && (
                <PulseDot
                  role="status"
                  aria-label={`${metric.pulseCount} unviewed`}
                  title="Unviewed"
                >
                  <PulseCount>{metric.pulseCount > 99 ? '99+' : metric.pulseCount}</PulseCount>
                </PulseDot>
              )}
            </MetricHeader>

            <MetricValueRow>
              <MetricValue>{metric.value}</MetricValue>
              {/* <MetricSubtitle $accent={metric.subtitleAccent}>{metric.subtitle}</MetricSubtitle> */}
            </MetricValueRow>

            <MetricFooter>
              <MetricFooterText></MetricFooterText>
              <MetricArrow>
                <ArrowRightIcon />
              </MetricArrow>
            </MetricFooter>
          </MetricCard>
        ))}
      </MetricsGrid>
    );
  };

  return (
    <DashboardWrapper>
      <DashboardContainer>
        <PageHeader>
          <HeaderLeft>
            <TitleRow>
              <Title>Dashboard</Title>
            </TitleRow>
            <Description>Support workspace overview and customer queue telemetry</Description>
          </HeaderLeft>
        </PageHeader>

        {renderContent()}
      </DashboardContainer>
    </DashboardWrapper>
  );
};

export default AdminDashboard;
